import { Component } from '@angular/core';
import { Router } from '@angular/router';

// RxJS
import { Subject, takeUntil } from 'rxjs';

// Services
import { ProjectService } from '@modules/project/services/project.service';
import { UsersService } from '@modules/users/services/users.service';

// Components
import { CreateProjectComponent } from '../create-project/create-project.component';

// Types
import { Project } from '@modules/project/types/project';
import { MatDialog } from '@angular/material/dialog';
import { UserPermissions } from '@modules/users/types/user';

@Component({
    selector: 'app-select-project',
    templateUrl: './select-project.component.html',
    styleUrl: './select-project.component.less',
    standalone: false
})
export class SelectProjectComponent {

  // Public
  public currentProject?: Project;
  public projects: Project[] = [];
  public isOpen = false;
  public permissions?: UserPermissions[];

  // Private
  private alive = new Subject<void>();

  /**
   * Constructor
   */

  constructor(
    private projectService: ProjectService,
    private usersService: UsersService,
    private dialog: MatDialog,
    private router: Router,
  ) {
  }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {
    // Get Project
    this.projectService.getCurrentProject()
      .pipe(takeUntil(this.alive))
      .subscribe(project => this.currentProject = project);
    // Get Projects
    this.projectService.getProjects()
      .pipe(takeUntil(this.alive))
      .subscribe(projects => this.projects = projects.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    // Get Permissions
    this.usersService.getPermissions()
      .pipe(takeUntil(this.alive))
      .subscribe(permissions => this.permissions = permissions);
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  selectProject(project: Project): void {
    this.isOpen = false;
    if (!project.id || project.id === this.currentProject?.id) { return; }
    // Navigate only — ProjectGuard reads the new id off the URL and updates ProjectService, so
    // there is one writer rather than two.
    this.router.navigate(['/', project.id, ...this.getTargetSegments()]);
  }

  /**
   * The same screen under the new project. A record id means nothing in another project, so
   * record-specific routes fall back to the list.
   */
  private getTargetSegments(): string[] {
    const segments = this.router.url.split('?')[0].split('/').filter(Boolean).slice(1);
    if (!segments.length || segments[0] === 'records') { return ['records']; }
    return segments;
  }

  createProject(): void {
    this.dialog.open(CreateProjectComponent, {data: {}, disableClose: true});
  }

}
